import type { BadgeVariant } from 'components/partials/badge/badge';

import type {
  CaptureListTab,
  CardActionKey,
  CriticalPopulationsView,
  DecisionActionConfig,
  DecisionActionKey,
} from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations.types';

import type { CriticalPopulationResponseStatus } from 'api/generated/schemas';

export const CRITICAL_POPULATIONS_SLUG = 'critical-populations';
export const VIEW_QUERY_KEY = 'view';
export const DEFAULT_VIEW: CriticalPopulationsView = 'active';

export const MAX_DIMENSIONS = 4;
export const INITIAL_CREATE_BLOCKS = 2;
export const NOTE_MAX_LENGTH = 500;
export const TITLE_MAX_LENGTH = 250;
export const PAGE_TITLE = 'Critical elements';
export const SELECT_MENU_Z_INDEX = 800;
export const YEAR_OPTIONS_FUTURE = 3;
export const YEAR_OPTIONS_PAST = 2;

export const NOTE_REQUIRED_MESSAGE = 'Reason is required';
export const TITLE_REQUIRED_MESSAGE = 'Critical Element name is required';
export const TITLE_TOO_LONG_MESSAGE = `Critical Element name must be ${TITLE_MAX_LENGTH} characters or fewer`;
export const NOTE_TOO_LONG_MESSAGE = `Reason must be ${NOTE_MAX_LENGTH} characters or fewer`;

export const TAB_ORDER: CriticalPopulationsView[] = [
  'active',
  'evaluating',
  'deferred',
  'configuration',
];

export const TAB_LABELS: Record<CriticalPopulationsView, string> = {
  active: 'Active',
  evaluating: 'Evaluating',
  deferred: 'Deferred',
  configuration: 'Configuration',
};

export const STATUS_TO_TAB: Partial<
  Record<CriticalPopulationResponseStatus, CaptureListTab>
> = {
  PROMOTED: 'active',
  GENERATED: 'evaluating',
  REJECTED: 'deferred',
  DEFERRED: 'deferred',
};

export const EMPTY_TEXT: Record<CaptureListTab, string> = {
  active: 'There are no active items yet.',
  evaluating: 'There are no evaluating items yet.',
  deferred: 'There are no deferred items yet.',
};

export const EDIT_STATUS_BADGE: Partial<
  Record<
    CriticalPopulationResponseStatus,
    { text: string; variant: BadgeVariant }
  >
> = {
  GENERATED: { text: 'Pending', variant: 'neutral-outlined' },
  PROMOTED: { text: 'Active', variant: 'success-filled' },
};

export const SKELETON_BY_TAB: Record<
  CaptureListTab,
  {
    cardCount: number;
    cardHeightClass: string;
    hasReasonBlock: boolean;
    hasToolbar: boolean;
    buttonCount: number;
  }
> = {
  active: {
    cardCount: 2,
    cardHeightClass: 'h-60',
    hasReasonBlock: true,
    hasToolbar: false,
    buttonCount: 1,
  },
  evaluating: {
    cardCount: 3,
    cardHeightClass: 'h-44',
    hasReasonBlock: false,
    hasToolbar: true,
    buttonCount: 2,
  },
  deferred: {
    cardCount: 2,
    cardHeightClass: 'h-60',
    hasReasonBlock: true,
    hasToolbar: false,
    buttonCount: 2,
  },
};

export const NOTE_TEXTAREA_STYLES = 'min-h-[88px] py-1 pl-2.5 pr-1';
export const NOTE_TEXTAREA_ROWS = 3;

export const TAB_PANEL_FILL_CLASS =
  'flex min-h-[calc(100vh-19.25rem)] flex-col';
export const EMPTY_STATE_CLASS =
  'h-auto flex-1 rounded-[10px] border border-dashed border-gray-300 bg-gray-50';
export const EMPTY_STATE_TEXT_CLASS =
  'mt-0 text-base font-semibold leading-6 text-gray-700';
export const EMPTY_STATE_IMAGE_CLASS = 'size-20';

export const DELETABLE_TABS: readonly CaptureListTab[] = [
  'active',
  'evaluating',
];

export const CARD_ACTIONS_BY_TAB = {
  active: ['edit', 'defer-active'],
  evaluating: ['promote-evaluating', 'edit', 'defer-evaluating'],
  deferred: ['promote-deferred', 'edit'],
} as const satisfies Record<CaptureListTab, readonly CardActionKey[]>;

const PROMOTE_TOAST = {
  successTitle: 'Critical Element moved to Active',
  successMessage: 'It’s now available in the Active tab.',
};

const DEFER_TOAST = {
  successTitle: 'Critical Element deferred',
  successMessage: 'You can return to it in the Deferred tab.',
};

export const DECISION_ACTIONS = {
  'promote-evaluating': {
    targetStatus: 'PROMOTED',
    title: 'Promote the recommendation',
    description: 'Critical Element will be enrolled in active monitoring.',
    field: {
      label: 'Reason',
      helper: 'Helps MARK learn from your decision',
    },
    confirmLabel: 'Promote',
    confirmVariant: 'brand-filled',
    ...PROMOTE_TOAST,
  },
  'defer-evaluating': {
    targetStatus: 'REJECTED',
    title: 'Defer this recommendation',
    description:
      'Critical Element will be saved but not enrolled in monitoring. You can revisit it anytime',
    field: { label: 'Reason' },
    confirmLabel: 'Defer',
    confirmVariant: 'brand-filled',
    ...DEFER_TOAST,
  },
  'defer-active': {
    targetStatus: 'REJECTED',
    title: 'Defer this Critical Element',
    description:
      'The Critical Element will be removed from active monitoring. You can revisit it anytime from the Deferred tab',
    field: { label: 'Reason' },
    confirmLabel: 'Defer',
    confirmVariant: 'neutral-outlined',
    ...DEFER_TOAST,
  },
  'promote-deferred': {
    targetStatus: 'PROMOTED',
    title: 'Promote',
    description: 'Critical Element will be enrolled in active monitoring.',
    field: { label: 'Reason' },
    confirmLabel: 'Promote',
    confirmVariant: 'brand-filled',
    ...PROMOTE_TOAST,
  },
} as const satisfies Record<DecisionActionKey, DecisionActionConfig>;

export const CARD_ACTION_LABELS: Record<CardActionKey, string> = {
  edit: 'Edit',
  'promote-evaluating': 'Promote',
  'defer-evaluating': 'Defer',
  'defer-active': 'Defer',
  'promote-deferred': 'Promote',
};

export const TITLE_PREVIEW_PLACEHOLDER =
  'Will be generated once all selections are made...';

export const PREVIEW_ERROR_FALLBACK =
  'This selection cannot be used to generate a Critical Element.';

export const CREATE_NOTE_HELPER =
  'Explain why this Critical Element is needed. This note is stored for review and content.';

export const SAVE_ERROR_TITLE = "Couldn't save";
export const SAVE_ERROR_FALLBACK = 'Something went wrong. Please try again.';
export const CREATE_SUCCESS = {
  title: 'Critical Element moved to Active',
  message: 'It’s now available in the Active tab.',
};
export const UPDATE_SUCCESS = {
  title: 'Critical Element updated',
  message: 'Changes saved.',
};

export const DELETE_SUCCESS = { title: 'Critical Element deleted' };
